import * as React from 'react';
import { useSnackbar } from 'notistack';
import useAuth from 'hooks/useAuth';
import axios from '../../utils/axios';
import { isMaster } from '../../utils/perfil';
import { AvatarIniciais, Num } from './index';
import Modal, { BotaoPrimario, BotaoSecundario, classesEntrada } from './Modal';
import { HistoricoGPS } from '../historicoGPS';
import { cn } from '../../lib/utils';

/* ============================================================================
   Atendimento do ticket: ler a conversa, responder e encerrar.

   POR QUE PRECISOU EXISTIR: a tela de Tickets listava, contava e abria ticket -
   e paravam ali. Nao havia como ABRIR um ticket para ler o que a pessoa escreveu,
   nem responder, nem fechar. Clicar na linha nao fazia nada. Ou seja: dava para
   registrar pedidos de suporte e nao dava para atende-los.

   As tres rotas ja existiam no backend, sem nenhum chamador no front:
     GET  /tickets/:codigo            -> ticket + mensagens (ASC por data)
     POST /tickets/:codigo/mensagens  -> { mensagem }
     POST /tickets/:codigo/status     -> { status }

   REGRAS DE PERMISSAO - copiadas do service, nao inventadas aqui
   (tickets.service.ts: `responder` e `mudarStatus`):

     - atendente (Administrador ou Master) responde qualquer ticket e move para
       qualquer status;
     - solicitante responde o proprio ticket e so pode FECHAR ou REABRIR;
     - ticket FECHADO nao muda de status por resposta.

   A tela desabilita o que o backend recusaria. Isso nao substitui a checagem de
   la - e para nao oferecer um botao que devolve erro.

   O QUE A TELA **NAO** FAZ, de proposito: nao muda status ao abrir o modal. Seria
   comodo marcar EM_ATENDIMENTO ao ler, mas isso gravaria em ticket alheio por um
   clique de leitura - e quem abriu para conferir viraria "atendente" sem querer.
   O botao "Assumir" faz isso explicitamente.
   ============================================================================ */

export const TICKET_STATUS = {
  ABERTO: 'ABERTO',
  EM_ATENDIMENTO: 'EM_ATENDIMENTO',
  RESPONDIDO: 'RESPONDIDO',
  FECHADO: 'FECHADO',
} as const;

const STATUS_LABEL: Record<string, string> = {
  ABERTO: 'Aberto',
  EM_ATENDIMENTO: 'Em atendimento',
  RESPONDIDO: 'Respondido',
  FECHADO: 'Fechado',
};

const STATUS_TOM: Record<string, string> = {
  ABERTO: 'bg-info-soft text-info',
  EM_ATENDIMENTO: 'bg-warn-soft text-warn',
  RESPONDIDO: 'bg-ok-soft text-ok',
  FECHADO: 'bg-soft text-faint',
};

type Mensagem = {
  codigo: number;
  idTicket: number;
  idUsuarioAutor: number;
  mensagem: string;
  createdAt: string;
  autorNome?: string;
};

type TicketDetalhe = {
  codigo?: number;
  numero?: number;
  assunto?: string;
  descricao?: string;
  status?: string;
  prioridade?: string;
  prazoSla?: string | null;
  slaVencido?: boolean;
  fechadoEm?: string | null;
  createdAt?: string;
  idUsuarioSolicitante?: number;
  solicitanteNome?: string;
  mensagens?: Mensagem[];
};

function data(valor?: string | null) {
  if (!valor) return '—';
  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(d);
}

export default function ModalTicket({
  aberto,
  codigo,
  onFechar,
  onMudou,
}: {
  aberto: boolean;
  codigo?: number | null;
  onFechar: () => void;
  /** Chamado a cada gravacao, para a listagem e os contadores recarregarem. */
  onMudou?: () => void;
}) {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [ticket, setTicket] = React.useState<TicketDetalhe>({});
  const [carregando, setCarregando] = React.useState(false);
  const [texto, setTexto] = React.useState('');
  const [enviando, setEnviando] = React.useState(false);
  const [mudandoStatus, setMudandoStatus] = React.useState('');

  const meuId = Number((user as any)?.codigo || (user as any)?.id || 0);
  const solicitante = Number(ticket.idUsuarioSolicitante || 0) === meuId;
  /* Atendente = quem abre um ticket que nao e seu (30/09/2026). A API so deixa
     ver os tickets das regionais de quem pede, entao estar aqui ja e ter acesso;
     antes so Administrador e Master atendiam. */
  const atendente = isMaster(user) || (Boolean(ticket.codigo) && !solicitante);
  const fechado = ticket.status === TICKET_STATUS.FECHADO;

  const carregar = React.useCallback(async () => {
    if (!codigo) return;
    setCarregando(true);
    try {
      const r = await axios.get<TicketDetalhe>(`/tickets/${codigo}`);
      setTicket(r.data || {});
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Não foi possível abrir o ticket.', { variant: 'error' });
    } finally {
      setCarregando(false);
    }
  }, [codigo, enqueueSnackbar]);

  React.useEffect(() => {
    if (!aberto) return;
    setTexto('');
    setTicket({});
    carregar();
  }, [aberto, carregar]);

  async function responder() {
    const mensagem = texto.trim();
    if (!mensagem || !codigo) return;
    setEnviando(true);
    try {
      await axios.post(`/tickets/${codigo}/mensagens`, { mensagem });
      setTexto('');
      await carregar();
      onMudou?.();
      enqueueSnackbar('Resposta enviada.', { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Não foi possível enviar a resposta.', { variant: 'error' });
    } finally {
      setEnviando(false);
    }
  }

  async function mudarStatus(status: string) {
    if (!codigo) return;
    setMudandoStatus(status);
    try {
      await axios.post(`/tickets/${codigo}/status`, { status });
      await carregar();
      onMudou?.();
      enqueueSnackbar(
        status === TICKET_STATUS.FECHADO ? 'Ticket encerrado.' : `Ticket movido para ${STATUS_LABEL[status] || status}.`,
        { variant: 'success' }
      );
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Não foi possível mudar o status.', { variant: 'error' });
    } finally {
      setMudandoStatus('');
    }
  }

  /* A DESCRICAO DO TICKET ENTRA NA CONVERSA como primeira fala, e nao num bloco
     separado acima dela: ela E a primeira mensagem do solicitante. Em bloco
     separado, a leitura comeca duas vezes - uma no "assunto/descricao" e outra na
     primeira resposta. */
  const conversa = React.useMemo(() => {
    const lista: Array<{ chave: string; autor: string; quando?: string; texto: string; doSolicitante: boolean }> = [];
    if (ticket.descricao)
      lista.push({
        chave: 'descricao',
        autor: ticket.solicitanteNome || 'Solicitante',
        quando: ticket.createdAt,
        texto: ticket.descricao,
        doSolicitante: true,
      });
    (ticket.mensagens || []).forEach((m) =>
      lista.push({
        chave: `m-${m.codigo}`,
        autor: m.autorNome || (Number(m.idUsuarioAutor) === Number(ticket.idUsuarioSolicitante) ? 'Solicitante' : 'Suporte'),
        quando: m.createdAt,
        texto: m.mensagem,
        doSolicitante: Number(m.idUsuarioAutor) === Number(ticket.idUsuarioSolicitante),
      })
    );
    return lista;
  }, [ticket]);

  const podeResponder = (atendente || solicitante) && !fechado;
  // Fechar: atendente sempre; solicitante so o proprio ticket (regra do service).
  // Encerrar e so do Master (a API recusa os demais).
  const podeFechar = !fechado && isMaster(user);
  const podeReabrir = fechado && (atendente || solicitante);

  return (
    <Modal
      aberto={aberto}
      titulo={ticket.numero ? `Ticket #${ticket.numero}` : 'Ticket'}
      descricao={ticket.assunto || (carregando ? 'Carregando...' : '—')}
      largura="max-w-[860px]"
      onFechar={onFechar}
      rodape={
        <>
          <BotaoSecundario onClick={onFechar}>Fechar janela</BotaoSecundario>

          {/* "Assumir" e explicito: ler nao muda status (ver o comentario no topo). */}
          {atendente && !fechado && ticket.status === TICKET_STATUS.ABERTO && (
            <BotaoSecundario
              onClick={() => mudarStatus(TICKET_STATUS.EM_ATENDIMENTO)}
              disabled={Boolean(mudandoStatus)}
            >
              {mudandoStatus === TICKET_STATUS.EM_ATENDIMENTO ? 'Assumindo...' : 'Assumir atendimento'}
            </BotaoSecundario>
          )}

          {podeReabrir && (
            <BotaoSecundario onClick={() => mudarStatus(TICKET_STATUS.ABERTO)} disabled={Boolean(mudandoStatus)}>
              {mudandoStatus === TICKET_STATUS.ABERTO ? 'Reabrindo...' : 'Reabrir'}
            </BotaoSecundario>
          )}

          {podeFechar && (
            <BotaoPrimario
              onClick={() => mudarStatus(TICKET_STATUS.FECHADO)}
              disabled={Boolean(mudandoStatus)}
              className="bg-ok hover:bg-ok/90"
            >
              {mudandoStatus === TICKET_STATUS.FECHADO ? 'Encerrando...' : '✓ Encerrar ticket'}
            </BotaoPrimario>
          )}
        </>
      }
    >
      {/* Cabecalho de dados: status, prioridade, prazo. O que decide se este
          ticket e o proximo a atender. */}
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'inline-flex h-[22px] items-center rounded-md px-2 text-[11px] font-bold',
            STATUS_TOM[String(ticket.status)] || 'bg-soft text-muted-foreground'
          )}
        >
          {STATUS_LABEL[String(ticket.status)] || ticket.status || '—'}
        </span>

        {ticket.prioridade && (
          <span className="text-[12px] text-muted-foreground">
            Prioridade <strong className="text-ink">{ticket.prioridade}</strong>
          </span>
        )}

        {/* PRAZO SO ENQUANTO ELE VALE (11/09/2026).

            Num ticket ja encerrado, "Prazo SLA 18/09" ao lado de "Encerrado em
            11/09" e leitura contraditoria: parece um compromisso que ainda
            corre, quando o assunto acabou. Fechado, o prazo vira passado - e o
            texto diz isso, em cinza, sem o alarme de vencido.

            Nao some: em auditoria interessa saber qual era o prazo e se ele foi
            cumprido. O que muda e o tempo do verbo. */}
        <span
          className={cn(
            'text-[12px]',
            !fechado && ticket.slaVencido ? 'font-bold text-danger' : 'text-muted-foreground'
          )}
        >
          {fechado ? 'Prazo era ' : 'Prazo SLA '}
          <Num>{data(ticket.prazoSla)}</Num>
          {!fechado && ticket.slaVencido ? ' · vencido' : ''}
        </span>

        {ticket.fechadoEm && (
          <span className="text-[12px] text-muted-foreground">
            Encerrado em <Num>{data(ticket.fechadoEm)}</Num>
          </span>
        )}
      </div>

      {/* CONVERSA */}
      <div className="max-h-[46vh] space-y-2 overflow-y-auto rounded-lg border border-line bg-surface-2 p-3">
        {carregando && <p className="py-6 text-center text-[12px] text-muted-foreground">Carregando conversa...</p>}

        {/* Sem `codigo` o ticket nao carregou: dizer "nenhuma mensagem" seria afirmar um fato falso. */}
        {!carregando && !ticket?.codigo && (
          <p className="py-6 text-center text-[12px] text-danger">Não foi possível carregar a conversa deste ticket.</p>
        )}
        {!carregando && !!ticket?.codigo && conversa.length === 0 && (
          <p className="py-6 text-center text-[12px] text-muted-foreground">Nenhuma mensagem neste ticket.</p>
        )}

        {conversa.map((f) => (
          <div
            key={f.chave}
            className={cn(
              'rounded-lg border p-2.5',
              /* O DESTAQUE DO SUPORTE ERA CINZA (11/09/2026).

                 Era `bg-primary/10`, e a classe EXISTE no CSS - conferido no
                 build, nao e o caso de utilitaria que nao gera. O problema e
                 outro: `--primary` e `214 43% 20%`, um azul-marinho escuro, e
                 10% dele sobre branco da rgb(232,234,237) - cinco pontos de
                 diferenca entre os canais, ou seja, CINZA.

                 O comentario original dizia que "quem falou precisa ser
                 respondido antes de ler o texto", e era exatamente isso que
                 nao acontecia: o cartao do suporte ficava quase igual ao do
                 solicitante.

                 `info-soft` e um azul SATURADO da paleta (a mesma troca feita
                 na trilha do ModalPassos hoje): tingir com ele da azul de
                 verdade. Tom claro de cor escura nao produz cor, produz cinza -
                 e este projeto ja tropecou nisso tres vezes. */
              f.doSolicitante ? 'border-line bg-surface' : 'border-info bg-info-soft'
            )}
          >
            <div className="mb-1 flex items-center gap-2">
              <AvatarIniciais nome={f.autor} className="h-5 w-5 text-[10px]" />
              <span className="text-[12px] font-bold">{f.autor}</span>
              <Num className="text-[11px] text-faint">{data(f.quando)}</Num>
            </div>
            {/* `whitespace-pre-wrap`: a pessoa escreve em linhas, e sem isto o
                relato vira um paragrafo unico. */}
            <p className="whitespace-pre-wrap text-[12px] leading-[1.55] text-ink">{f.texto}</p>
          </div>
        ))}
      </div>

      {/* RESPOSTA */}
      {podeResponder ? (
        <div>
          <label htmlFor="ticket-resposta" className="mb-1 block text-[12px] font-semibold">
            Responder
          </label>
          <textarea
            id="ticket-resposta"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={4}
            maxLength={4000}
            placeholder={atendente ? 'Resposta ao solicitante...' : 'Complemento ou resposta ao suporte...'}
            className={cn(classesEntrada, 'h-auto py-2 leading-[1.5]')}
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-[11px] text-faint">
              {atendente
                ? 'Responder marca o ticket como Respondido.'
                : 'Sua resposta devolve o ticket para Em atendimento.'}
            </span>
            <BotaoPrimario onClick={responder} disabled={enviando || !texto.trim()}>
              {enviando ? 'Enviando...' : 'Enviar resposta'}
            </BotaoPrimario>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px] text-muted-foreground">
          {fechado
            ? 'Ticket encerrado. Reabra para voltar a responder.'
            : 'Seu perfil não responde este ticket.'}
        </div>
      )}

      {/* HISTORICO (29/09/2026, a pedido: "historico nao aparece quem fez").

          Ate agora o modulo de tickets nao gravava auditoria NENHUMA - era o
          unico cadastro do sistema sem trilha. O backend passou a registrar
          abertura, resposta, fechamento, reabertura e mudanca de status, e esta
          e a mesma peca usada em APK, grupo, politica e usuario.

          FICA ABAIXO DAS MENSAGENS, e nao numa aba: a conversa e o assunto
          principal do ticket, e o historico e a leitura de auditoria que se faz
          DEPOIS de entender o caso. Numa aba, so quem soubesse que ela existe
          olharia - e a pergunta que originou isto ("quem fez?") aparece
          justamente enquanto se le a conversa.

          So aparece com o ticket carregado: sem codigo nao ha o que buscar, e a
          consulta voltaria vazia dando a impressao de que nao houve acao. */}
      {ticket.codigo ? (
        <div className="mt-4 border-t border-line pt-3">
          <HistoricoGPS tipo={'TICKET'} chave={ticket.codigo} title="Histórico do ticket" />
        </div>
      ) : null}
    </Modal>
  );
}
